import { useCallback, useMemo } from 'react'
import {
  caseStudies,
  getCaseStudiesByCategory,
  getCaseStudyBySlug,
  getCaseStudyStats,
  getFeaturedCaseStudies,
  type CaseStudy,
} from '../data/caseStudies'
import { CaseStudyStats } from '../types/caseStudy'

/**
 * Custom hook for managing case study data with optimized filtering and search
 *
 * Features:
 * - Memoized filtering for performance
 * - Search functionality across multiple fields
 * - Category-based filtering
 * - Featured case studies filtering
 * - Pre-calculated statistics
 */

export interface UseCaseStudiesOptions {
  category?: string
  featured?: boolean
  searchTerm?: string
}

export interface UseCaseStudiesReturn {
  caseStudies: CaseStudy[]
  allCaseStudies: CaseStudy[]
  featuredCaseStudies: CaseStudy[]
  stats: CaseStudyStats
  isEmpty: boolean
  isLoading: boolean
}

export function useCaseStudies(
  options: UseCaseStudiesOptions = {}
): UseCaseStudiesReturn {
  const { category = 'all', featured = false, searchTerm = '' } = options

  // Memoize filtered case studies
  const filteredCaseStudies = useMemo(() => {
    let filtered = caseStudies

    // Filter by category
    if (category !== 'all') {
      filtered = getCaseStudiesByCategory(category)
    }

    // Filter by featured status
    if (featured) {
      filtered = filtered.filter(cs => cs.featured)
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(
        cs =>
          cs.title.toLowerCase().includes(term) ||
          cs.subtitle.toLowerCase().includes(term) ||
          cs.overview.problem.toLowerCase().includes(term) ||
          cs.overview.solution.toLowerCase().includes(term) ||
          cs.technologies.some(tech =>
            tech.name.toLowerCase().includes(term)
          ) ||
          cs.sections.some(
            section =>
              section.title.toLowerCase().includes(term) ||
              section.content.toLowerCase().includes(term)
          )
      )
    }

    return filtered
  }, [category, featured, searchTerm])

  // Memoize featured case studies
  const featuredCaseStudies = useMemo(() => {
    return getFeaturedCaseStudies()
  }, [])

  // Memoize statistics
  const stats = useMemo(() => {
    return getCaseStudyStats()
  }, [])

  const isEmpty = filteredCaseStudies.length === 0
  const isLoading = false // For future async data loading

  return {
    caseStudies: filteredCaseStudies,
    allCaseStudies: caseStudies,
    featuredCaseStudies,
    stats,
    isEmpty,
    isLoading,
  }
}

/**
 * Hook for individual case study by slug
 */
export function useCaseStudy(slug: string): {
  caseStudy: CaseStudy | undefined
  isLoading: boolean
  error: string | null
} {
  const caseStudy = useMemo(() => {
    return getCaseStudyBySlug(slug)
  }, [slug])

  return {
    caseStudy,
    isLoading: false,
    error: caseStudy ? null : 'Case study not found',
  }
}

/**
 * Hook for case study categories and metadata
 */
export function useCaseStudyCategories() {
  const categories = useMemo(() => {
    const categoryMap = {
      all: 'All Case Studies',
      enterprise: 'Enterprise Systems',
      architecture: 'Software Architecture',
      performance: 'Performance Optimization',
      'ai-integration': 'AI Integration',
      scalability: 'Scalability Solutions',
    }

    return Object.entries(categoryMap).map(([id, label]) => ({
      id,
      label,
      count: getCaseStudiesByCategory(id).length,
    }))
  }, [])

  const getCategoryLabel = useCallback(
    (categoryId: string) => {
      const category = categories.find(cat => cat.id === categoryId)
      return category?.label || categoryId
    },
    [categories]
  )

  const getCategoryCount = useCallback(
    (categoryId: string) => {
      const category = categories.find(cat => cat.id === categoryId)
      return category?.count || 0
    },
    [categories]
  )

  return {
    categories,
    getCategoryLabel,
    getCategoryCount,
  }
}

/**
 * Hook for case study search functionality
 */
export function useCaseStudySearch(searchTerm: string) {
  return useMemo(() => {
    if (!searchTerm.trim()) {
      return {
        results: caseStudies,
        totalResults: caseStudies.length,
        searchTime: 0,
      }
    }

    const startTime = performance.now()
    const term = searchTerm.toLowerCase()

    const results = caseStudies.filter(cs => {
      // Search in title and subtitle
      if (
        cs.title.toLowerCase().includes(term) ||
        cs.subtitle.toLowerCase().includes(term)
      ) {
        return true
      }

      // Search in overview
      if (
        cs.overview.problem.toLowerCase().includes(term) ||
        cs.overview.solution.toLowerCase().includes(term) ||
        cs.overview.impact.toLowerCase().includes(term)
      ) {
        return true
      }

      // Search in technologies
      if (
        cs.technologies.some(
          tech =>
            tech.name.toLowerCase().includes(term) ||
            tech.description?.toLowerCase().includes(term)
        )
      ) {
        return true
      }

      // Search in sections
      if (
        cs.sections.some(
          section =>
            section.title.toLowerCase().includes(term) ||
            section.content.toLowerCase().includes(term)
        )
      ) {
        return true
      }

      return false
    })

    const searchTime = performance.now() - startTime

    return {
      results,
      totalResults: results.length,
      searchTime: Math.round(searchTime * 100) / 100,
    }
  }, [searchTerm])
}
